import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, allVideos } from '@/mocks/data';

function formatNumber(n: number): string {
  if (n >= 10000) {
    return (n / 10000).toFixed(n >= 100000 ? 0 : 1) + '万';
  }
  return n.toString();
}

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const video = allVideos.find((v) => v.id === Number(id));

  if (!video) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 text-center py-20">
          <p className="text-foreground-500">内容不存在或已下架</p>
          <Link to="/" className="text-primary-500 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === video.category);
  const relatedVideos = allVideos
    .filter((v) => v.category === video.category && v.id !== video.id)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: category?.name || video.category, to: `/category/${video.category}` },
    { label: video.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    author: {
      '@type': 'Person',
      name: video.author,
    },
    datePublished: video.publishDate,
    url: `https://sweetvid.github.io/article/${video.id}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Article Header */}
          <article className="max-w-4xl mx-auto px-4 md:px-6 pb-14 md:pb-20">
            <h1 className="text-xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              {video.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <img
                  src={video.authorAvatar}
                  alt={video.author}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span className="text-sm text-foreground-700 font-medium">{video.author}</span>
              </div>
              <span className="text-xs text-foreground-400">{video.publishDate}</span>
              <div className="flex items-center gap-3 text-xs text-foreground-400">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  {formatNumber(video.views)} 次观看
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-heart-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  {formatNumber(video.likes)} 赞
                </span>
              </div>
            </div>

            {/* Thumbnail */}
            <div className="relative aspect-video rounded-lg overflow-hidden mb-8 bg-background-200">
              <img
                src={video.thumbnail}
                alt={video.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 backdrop-blur-sm cursor-pointer hover:bg-primary-600 transition-colors duration-200">
                  <i className="ri-play-fill text-white text-2xl md:text-3xl w-6 h-6 md:w-8 md:h-8 flex items-center justify-center"></i>
                </div>
              </div>
              <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded bg-black/70 text-white text-xs">
                {video.duration}
              </div>
            </div>

            {/* Description */}
            <div className="bg-background-100 rounded-lg p-5 md:p-6 mb-8">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{video.description}</p>
            </div>

            {/* Content */}
            <div className="prose prose-sm md:prose-base max-w-none mb-8">
              <p className="text-foreground-700 leading-relaxed">{video.content}</p>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mb-8">
              {video.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4 py-6 border-t border-background-200">
              <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 whitespace-nowrap transition-colors duration-200 cursor-pointer">
                <i className="ri-heart-fill w-4 h-4 flex items-center justify-center"></i>
                点赞 ({formatNumber(video.likes)})
              </button>
              <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 whitespace-nowrap transition-colors duration-200 cursor-pointer">
                <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center"></i>
                分享
              </button>
              <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 whitespace-nowrap transition-colors duration-200 cursor-pointer">
                <i className="ri-bookmark-line w-4 h-4 flex items-center justify-center"></i>
                收藏
              </button>
            </div>

            {/* Related Videos */}
            {relatedVideos.length > 0 && (
              <section className="border-t border-background-200 pt-8">
                <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedVideos.map((v) => (
                    <Link
                      key={v.id}
                      to={`/article/${v.id}`}
                      className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-100 cursor-pointer border border-background-200"
                    >
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={v.thumbnail}
                          alt={v.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px]">
                          {v.duration}
                        </div>
                      </div>
                      <div className="p-3">
                        <h3 className="text-xs md:text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                          {v.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-[10px] text-foreground-500">{v.author}</span>
                          <span className="text-[10px] text-foreground-400">{formatNumber(v.views)} 观看</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}